/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import { Collapse, IconButton, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import uuid from 'react-uuid';
import { SecurityRead, SharesAcquisitionWritable } from 'api';
import * as CTC from 'common/constants/cap-table';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import * as FOC from 'common/constants/fund-ownership';
import * as validationMessages from 'common/constants/messages/validations';
import { CONVERTIBLE_NOTES, OPTION, WARRANT } from 'common/constants/securityTypes';
import { useFormat } from 'common/hooks';
import { CustomContainer } from 'components';
import { LedgerDialog } from 'components/Dialogs';
import { DEFAULT_CURRENCY, DEFAULT_CURRENCY_SYMBOL } from 'components/FeaturedSpreadsheet/constants';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { useFundOwnershipContext } from 'context/FundOwnershipContext';
import { LOAN_VALUE } from 'pages/CapTable/cap-table/components/convertible-notes-ledger/config/constants';
import {
  BASIS_3_VALUE,
  ISSUE_PRICE_VALUE,
} from 'pages/CapTable/cap-table/components/liquidation-preference-ledger/liq-pref-ledger-table-config/constants';
import { readLiquidationPreferenceConfig } from 'pages/CapTable/cap-table/utilities';
import calculateDividendsForMultipleDivRates from 'pages/CapTable/cap-table/utilities/calculateDividendsForMultipleDivRates';
import { LedgerConfig } from 'pages/CapTable/cap-table/utilities/readLiquidationPreferenceConfig.props';
import { CashDistributionLedgerAcquisition } from 'pages/CapTable/fund-ownership/components/cash-distribution/CashDistributionLedger.props';
import useFundOwnershipFundCell from 'pages/CapTable/fund-ownership/components/shares-ledger/hooks/useFundOwnershipFundCell';
import useSharesLedgerSecurity from 'pages/CapTable/fund-ownership/components/shares-ledger/hooks/useSharesLedgerSecurity';
import useSharesLedgerValidation from 'pages/CapTable/fund-ownership/components/shares-ledger/hooks/useSharesLedgerValidation';
import { AcquisitionCell } from 'pages/CapTable/fund-ownership/models/fund-ownership.models';
import { FundOwnershipCell } from 'pages/CapTable/fund-ownership/types';
import isModeledAfterNote from 'pages/CapTable/fund-ownership/utilities/isModeledAfterNote';
import { dividendsPerShareAtDate, getNumberValue, roundToN, slashFormat, toString } from 'utilities';
import { Outstanding, PurchasedTable, SoldTable } from './components';
import ConvertedTable from './components/converted-table/ConvertedTable';
import { AMOUNT_ALIAS, PROCEEDS_ALIAS, PURCHASE_DATE_ALIAS } from './constants';
import calculateConvertibleNoteOutstandingFromCells from './utilities/calculateConvertibleNoteOutstandingFromCells';
import calculateRegularOutstandingFromCells from './utilities/calculateRegularOutstandingFromCells';
import disableSecuritySelectionCell from './utilities/disableSecuritySelectionCell';
import { commonReverseParser } from './utilities/reverseParsers';
import { ReverseParsedDividendRateLedgerItem } from '../../../cap-table/components/dividend-rate-ledger/config/types';

const rowNumbers = {
  investmentDate: FOC.INVESTMENT_DATE_ROW_NUMBER,
  investmentCapital: FOC.INVESTMENT_CAPITAL_ROW_NUMBER,
  loanValue: FOC.LOAN_VALUE_ROW_NUMBER,
  shares: FOC.SHARES_ROW_NUMBER,
  proceedsFromSoldShares: FOC.PROCEEDS_FROM_SOLD_SHARES_ROW_NUMBER,
  liquidationPreferencePlus: FOC.LIQUIDATION_PREFERENCE_PLUS_ROW_NUMBER,
};

const isSoldDataValid = (data: Array<{ amount?: number | null; proceeds?: number | null }>) =>
  Array.isArray(data) &&
  data.length > 0 &&
  data.every(item => typeof item === 'object' && (AMOUNT_ALIAS in item || PROCEEDS_ALIAS in item));

export interface SharesLedgerProps {
  cell: FundOwnershipCell;
  closeDialog: () => void;
  investmentDates: string[];
  relatedSecurity?: number;
}

const SharesLedger = ({
  cell = {
    value: null,
    key: '',
  } as FundOwnershipCell,
  closeDialog,
  investmentDates,
}: SharesLedgerProps) => {
  const { cells, onCellsChanged } = useContext(FeaturedSpreadsheetContext);
  // state for cells of tables
  const [purchasedCells, setPurchasedCells] = useState<Record<string, FundOwnershipCell>>({});
  const [soldCells, setSoldCells] = useState<Record<string, FundOwnershipCell>>({});
  const [convertedCells, setConvertedCells] = useState<Record<string, FundOwnershipCell>>({});
  const {
    isDisabled,
    fundList,
    selectedMeasurementDate,
    measurementDate,
    tableData: {
      fundOwnership: { fund_ownership_detail },
    },
    fieldAttributes,
  } = useFundOwnershipContext();

  const sharesMaxDecimalPlaces = useMemo(
    () => Number(fieldAttributes?.get('fundownershipdetail')?.shares?.decimal_places ?? 2),
    [fieldAttributes]
  );

  const dataSourceKey = useMemo(() => `${cell.columnLegend! + FOC.SHARES_LEDGER_ROW_NUMBER}`, [cell.columnLegend]);
  const relatedSecurityId = useMemo(() => {
    if (cell?.isCustomSecurity) {
      return null;
    }
    if (cell?.relatedSecurity) {
      return Number(cell.relatedSecurity);
    }
    return Number(cells[cell.columnLegend! + FOC.SECURITY_ROW_NUMBER]?.value);
  }, [cell?.relatedSecurity, cells, dataSourceKey]);

  const { securities, currentSecurity } = useSharesLedgerSecurity({
    cells,
    cell,
    relatedSecurityId,
    isCustomSecurity: cell?.isCustomSecurity,
  });

  const isWarrantOrOption = [WARRANT, OPTION].includes(currentSecurity?.security_type ?? -1);

  const informationFromVariableLiqPrefLedger = useMemo(() => {
    const defaults = {
      multipleOf: ISSUE_PRICE_VALUE,
      yearfracBasis: BASIS_3_VALUE,
    };
    if (!currentSecurity.liquidation_preference_ledger_config || !measurementDate || !currentSecurity) {
      return defaults;
    }
    const { multipleOf, yearfracBasis } = readLiquidationPreferenceConfig(
      currentSecurity.liquidation_preference_ledger_config as LedgerConfig,
      measurementDate,
      currentSecurity.investment_date!,
      currentSecurity.issue_price!,
      currentSecurity.shares_outstanding!
    );
    return { multipleOf, yearfracBasis };
  }, [currentSecurity, measurementDate]);

  const multipleOfFactor = useMemo(() => {
    if (!currentSecurity) {
      return 1;
    }
    const { multipleOf } = informationFromVariableLiqPrefLedger;

    return multipleOf === ISSUE_PRICE_VALUE ? 1 : Number(currentSecurity.liquidation_preference);
  }, [informationFromVariableLiqPrefLedger, currentSecurity]);

  const yearfracBasis = useMemo(
    () => informationFromVariableLiqPrefLedger.yearfracBasis,
    [informationFromVariableLiqPrefLedger]
  );

  const dividendsPerShareAtDateMemo = useCallback(
    (investmentDate: string) =>
      dividendsPerShareAtDate({
        hasCumulativeDividends: !!currentSecurity?.has_dividends,
        hasCompounding: !!currentSecurity?.has_compounding,
        periodStartDate: investmentDate,
        periodEndDate: measurementDate,
        periodIssuePrice: Number(currentSecurity.issue_price),
        compoundingPeriods: getNumberValue(currentSecurity.compounding_period),
        dividendRate: getNumberValue(currentSecurity.dividend_rate),
        yearfracBasis,
      }),
    [currentSecurity, measurementDate, yearfracBasis]
  );

  const startingAcquisitions = useMemo(
    () =>
      (cell?.acquisitions as CashDistributionLedgerAcquisition[])?.map((acq: CashDistributionLedgerAcquisition) => {
        const tempRef = uuid();
        const sales = acq.sales.map(sale => ({ ...sale, acquisition_ref: tempRef, sale_ref: uuid() }));
        const conversions = acq.conversions.map(conversion => ({
          ...conversion,
          acquisition_ref: tempRef,
          conversion_ref: uuid(),
        }));
        return {
          ...acq,
          acquisition_ref: tempRef,
          sales,
          cash_distributions: acq.cash_distributions,
          conversions,
          deleted_sales: [],
          deleted_conversions: [],
        };
      }) || [],
    [cell]
  );

  const [acquisitionsData, setAcquisitionsData] = useState({
    acquisitions: startingAcquisitions,
  });
  const [deletedAcquisitions, setDeletedAcquisitions] = useState([]);

  // this will be used to synchronize input changes with the acquisitionsData state
  const [format] = useFormat({
    page: CAP_TABLE_CURRENCY_PAGE,
    units: `${DEFAULT_CURRENCY} ${DEFAULT_CURRENCY_SYMBOL}`,
  });

  const fund = useFundOwnershipFundCell({ cells, cell, fundList });

  // validation result per table
  const [isValidPurchasedTable, setIsValidPurchasedTable] = useState(true);
  const [isValidConvertedTable, setIsValidConvertedTable] = useState(true);
  const [isValidSoldTable, setIsValidSoldTable] = useState(true);
  const [isValidOutstandingTable, setIsValidOutstandingTable] = useState(true);

  const [isAlertVisible, setIsAlertVisible] = useState(false);

  const { currency } = format;

  const isDifferentCurrency = useMemo(() => fund?.currency && fund.currency !== format.currency.code, [fund, format]);

  const modeledAfterConvertibleNote = useMemo(() => {
    if (cell?.modeledAfterConvertibleNote) {
      return cell.modeledAfterConvertibleNote;
    }
    const referencedSecurity =
      securities.find(sec => toString(sec.id) === toString(relatedSecurityId)) || ({} as SecurityRead);
    return isModeledAfterNote({ securityInfo: referencedSecurity });
  }, [cell.modeledAfterConvertibleNote, securities, relatedSecurityId]);

  const convertibleNote = useMemo(() => {
    const referencedSecurity = (securities.find(sec => toString(sec.id) === toString(relatedSecurityId)) ||
      {}) as SecurityRead;
    const relatedNoteSecurity = securities.find(sec => sec.security_ref === referencedSecurity.security_group_ref);

    return relatedNoteSecurity?.convertible_notes?.find(
      note => note.security_group_ref === referencedSecurity.security_ref
    );
  }, [modeledAfterConvertibleNote, securities, relatedSecurityId]);

  const loanNote = useMemo(() => {
    if (convertibleNote?.model_as_equity === LOAN_VALUE) {
      return convertibleNote;
    }
    return null;
  }, [convertibleNote]);

  const loanValueRatio = useMemo(() => {
    if (loanNote) {
      return Number(loanNote.loan_value) / Number(loanNote.note_principle_amount);
    }
    return 1;
  }, [loanNote]);

  const outstandingCalculations = useMemo(() => {
    if (modeledAfterConvertibleNote) {
      return calculateConvertibleNoteOutstandingFromCells({
        purchasedCells: purchasedCells as Record<string, AcquisitionCell>,
        soldCells: soldCells as Record<string, AcquisitionCell>,
        convertedCells: convertedCells as Record<string, AcquisitionCell>,
        loanNote,
        loanValueRatio,
      });
    }
    return calculateRegularOutstandingFromCells({ purchasedCells, soldCells });
  }, [purchasedCells, soldCells, convertedCells, modeledAfterConvertibleNote, loanNote, loanValueRatio]);

  const outstandingShares = outstandingCalculations.sharesOutstanding;
  const outstandingCostBasis = outstandingCalculations.costBasisOutstanding;
  const outstandingAmount = outstandingCalculations.amountOutstanding;
  const { outstandingLoanValue, totalInvested } = outstandingCalculations;

  const reverseParsedAcquisitions = useMemo(() => {
    if (acquisitionsData.acquisitions && !isEmpty(purchasedCells)) {
      const isConvertibleNote = modeledAfterConvertibleNote ?? false;
      return commonReverseParser({
        purchasedCells: purchasedCells as Record<string, AcquisitionCell>,
        soldCells: soldCells as Record<string, AcquisitionCell>,
        convertedCells: convertedCells as Record<string, AcquisitionCell>,
        isConvertibleNote,
      });
    }

    return [];
  }, [purchasedCells, acquisitionsData, soldCells, convertedCells, modeledAfterConvertibleNote]);

  const updateFields = () => {
    const cellsChanged = [];
    const tmpState = { ...cells };
    const sales = reverseParsedAcquisitions?.map(acq => acq.sales).flat() ?? [];
    const referencedSecurity = (securities.find(sec => toString(sec.id) === toString(relatedSecurityId)) ||
      {}) as SecurityRead;

    let balanceSharesForNotes = 0;
    // calculate share balance in ledger for security modeled after note
    if (modeledAfterConvertibleNote && CONVERTIBLE_NOTES !== referencedSecurity.security_type) {
      // get the outstanding amount and the note principle amount and calculate a ratio
      // multiply that ratio by the expected shares from the convertible note
      // if the outstanding amount is greater than or equal to the note principle amount, just use the expected shares
      const sharesRatio = outstandingAmount / Number(convertibleNote?.note_principle_amount);
      balanceSharesForNotes =
        sharesRatio >= 1
          ? Number(convertibleNote?.expected_shares)
          : sharesRatio * Number(convertibleNote?.expected_shares);
    }
    // Update invested date
    const investmentDateKey = cell.columnLegend! + rowNumbers.investmentDate;
    const acquisitionsRef = reverseParsedAcquisitions;
    const investmentDate = acquisitionsRef?.length ? slashFormat(acquisitionsRef[0][PURCHASE_DATE_ALIAS]) : 'Not Set';
    cellsChanged.push({
      cell: tmpState[investmentDateKey],
      value: investmentDate,
    });

    // Update invested capital
    const investmentCapitalKey = cell.columnLegend! + rowNumbers.investmentCapital;
    cellsChanged.push({
      cell: tmpState[investmentCapitalKey],
      value: roundToN(totalInvested).toString(),
    });

    // Update Loan Value
    const loanValueKey = cell.columnLegend! + rowNumbers.loanValue;
    cellsChanged.push({
      cell: tmpState[loanValueKey],
      value: loanNote ? roundToN(outstandingLoanValue).toString() : '0',
    });

    // Update shares
    const sharesKey = cell.columnLegend! + rowNumbers.shares;
    cellsChanged.push({
      cell: tmpState[sharesKey],
      value: modeledAfterConvertibleNote ? balanceSharesForNotes.toString() : outstandingShares.toString(),
    });

    // Update Liquidation Preference Plus
    // multiply per liq pref if price + dividends config from security
    const liquidationPreferencePlusKey = cell.columnLegend! + rowNumbers.liquidationPreferencePlus;
    cellsChanged.push({
      cell: tmpState[liquidationPreferencePlusKey],
      value: `=${outstandingShares.toString()}*CAPTABLE("${CTC.ISSUE_PRICE_TITLE}", ${cell.columnLegend!}${
        FOC.SECURITY_ROW_NUMBER
      })*CAPTABLE("${CTC.LIQUIDATION_PREFERENCE_TITLE}", ${cell.columnLegend!}${FOC.SECURITY_ROW_NUMBER})+${multipleOfFactor}*${
        outstandingValuesForDividends.cumulative_dividends
      }`,
    });

    // Update proceeds from sold shares
    const proceedsFromSoldSharesKey = cell.columnLegend! + rowNumbers.proceedsFromSoldShares;
    let proceedsFromSoldShares = 0;

    if (isSoldDataValid(sales)) {
      proceedsFromSoldShares = sales
        .map(item => Number(modeledAfterConvertibleNote && !loanNote ? item.amount : item.proceeds))
        .reduce((prev, next) => prev + next, 0);
    }
    cellsChanged.push({
      cell: tmpState[proceedsFromSoldSharesKey],
      value: roundToN(proceedsFromSoldShares).toString(),
    });
    // Send cell change method
    onCellsChanged(cellsChanged);
  };

  const updateAcquisitionInColumnCells = useCallback(() => {
    // recover ids for objects
    const currentFod = fund_ownership_detail?.find(({ columnRef }) => columnRef === cell.columnRef);
    // need to make sure the children point to the right acquisitions
    const allSales = reverseParsedAcquisitions.map(acq => acq.sales).flat();
    const allConversions = reverseParsedAcquisitions.map(acq => acq.conversions ?? []).flat();

    const updatedAcquisitions = reverseParsedAcquisitions.map(
      acquisition =>
        ({
          ...acquisition,
          id:
            startingAcquisitions.find(
              (acq: CashDistributionLedgerAcquisition) => acq.acquisition_ref === acquisition.acquisition_ref
            )?.id ?? 0,
          sales: allSales.filter(sale => sale.acquisition_ref === acquisition.acquisition_ref),
          fund_ownership_detail: currentFod?.id ? currentFod.id : undefined,
          cash_distributions: acquisition.cash_distributions ?? [],
          conversions: allConversions.filter(conversion => conversion.acquisition_ref === acquisition.acquisition_ref),
        }) as SharesAcquisitionWritable
    );
    // this should update the acquisitions information for the whole column
    const { columnLegend: currentColumnLegend, sheet } = cell as {
      columnLegend: string;
      sheet: { cells: Record<string, FundOwnershipCell> };
    };

    const securityName = currentFod?.security_name ?? currentSecurity?.name ?? currentFod?.custom_security_name;

    Object.values(sheet.cells)
      .filter(({ columnLegend }) => currentColumnLegend === columnLegend)
      .forEach((cell: FundOwnershipCell) => {
        cell.acquisitions = updatedAcquisitions;
        cell.deleted_acquisitions = deletedAcquisitions;
        if (
          cell.alias === FOC.SECURITY_ALIAS &&
          updatedAcquisitions.length &&
          !isDisabled &&
          !currentFod?.is_custom_security
        ) {
          disableSecuritySelectionCell(cell, securityName);
        }
      });
  }, [cell, reverseParsedAcquisitions, fund_ownership_detail, startingAcquisitions, deletedAcquisitions]);

  const save = () => {
    // In convertible notes, the balance owned should be greater than 0, not equal to 0
    const isOutstandingAmountValid = convertibleNote ? outstandingAmount > 0 : outstandingAmount >= 0;

    // A positive balance does not make an incomplete or invalid conversion safe to save.
    if (
      isValidPurchasedTable &&
      isValidSoldTable &&
      isValidConvertedTable &&
      isValidOutstandingTable &&
      outstandingShares >= 0 &&
      outstandingCostBasis >= 0 &&
      isOutstandingAmountValid
    ) {
      const tmpParsedColumns = { ...cells };
      // any due to dynamic type of outstanding
      // if modeledAfterConvertibleNote, should change how outstanding is represented.
      let outstanding: any = {
        shares: outstandingShares,
        cost_basis: outstandingCostBasis,
      };

      if (currentSecurity?.has_dividends) {
        outstanding = {
          ...outstanding,
          cumulative_dividends: outstandingValuesForDividends.cumulative_dividends,
          total_shares_outstanding: outstandingValuesForDividends.total_shares_outstanding,
        };
      } else if (modeledAfterConvertibleNote) {
        outstanding = { amount: outstandingAmount };
      }
      const cellKey = cell.linkedCells?.values().next().value?.key;
      tmpParsedColumns[cellKey!].value = outstanding;
      updateAcquisitionInColumnCells();
      updateFields();
      closeDialog();
    } else {
      setIsAlertVisible(true);
    }
  };

  const { doFullValidation, requestValidation, updateValidationStatus } = useSharesLedgerValidation({
    tableCount: modeledAfterConvertibleNote && !loanNote ? 4 : 3,
    onValidated: save,
  });

  const outstandingValuesForDividends = useMemo(() => {
    if (currentSecurity?.has_dividends) {
      const cumulative_dividends =
        !currentSecurity.dividend_rate_ledger_items?.length
          ? dividendsPerShareAtDateMemo(currentSecurity.investment_date ?? '') * outstandingShares
          : calculateDividendsForMultipleDivRates(
              currentSecurity.dividend_rate_ledger_items as unknown as ReverseParsedDividendRateLedgerItem[],
              { ...currentSecurity, shares_outstanding: `${outstandingShares}` },
              measurementDate
          );
      // dividend payment type is 0 for cash and 1 for PIK
      const total_shares_outstanding =
        (Number(currentSecurity.dividend_payment_type) * cumulative_dividends) / Number(currentSecurity.issue_price) +
        outstandingShares;
      return { cumulative_dividends, total_shares_outstanding };
    }
    return { cumulative_dividends: 0, total_shares_outstanding: 0 };
  }, [reverseParsedAcquisitions, currentSecurity, outstandingShares, measurementDate, dividendsPerShareAtDateMemo]);

  const fundOwnershipDetailForColumn = useMemo(
    () => fund_ownership_detail?.find(({ columnRef }) => columnRef === cell.columnRef),
    [fund_ownership_detail, cell.columnRef]
  );

  const sharedProps = {
    cell,
    isAlertVisible,
    setIsAlertVisible,
    doFullValidation,
    updateValidationStatus,
    isDisabled,
    modeledAfterConvertibleNote,
    dataSourceKey,
    isDifferentCurrency,
    selectedMeasurementDate,
    fundCurrency: fund?.currency,
    acquisitionsData,
    setAcquisitionsData,
    reverseParsedAcquisitions,
    setDeletedAcquisitions,
    sourceSecurity: currentSecurity,
    sharesMaxDecimalPlaces,
    fundOwnershipDetailId: fundOwnershipDetailForColumn?.id,
  };

  const errorMessage = useMemo(() => {
    if (
      isValidPurchasedTable &&
      isValidSoldTable &&
      isValidConvertedTable &&
      !modeledAfterConvertibleNote &&
      (outstandingShares >= 0 || outstandingCostBasis >= 0)
    ) {
      return validationMessages.NEGATIVE_OUTSTANDINGS;
    }

    if (outstandingAmount <= 0) {
      return validationMessages.REFERENCED_VALUE_MUST_BE_POSITIVE('Balance Owned');
    }

    return validationMessages.FIX_BEFORE_CONTINUE;
  }, [
    outstandingShares,
    isValidSoldTable,
    isValidPurchasedTable,
    isValidConvertedTable,
    isValidOutstandingTable,
    modeledAfterConvertibleNote,
  ]);

  const titleLedger = useMemo(() => {
    if (modeledAfterConvertibleNote) {
      if (loanNote) {
        return 'Note - Investment Ledger';
      }
      return 'Notes - Investment Ledger';
    }
    if (cell?.isCustomSecurity) {
      return 'Custom Security Ownership Ledger';
    }
    return 'Shares Ledger';
  }, [cell, loanNote]);

  const disabledStatus = useMemo(
    () => isDisabled || acquisitionsData.acquisitions.length === 0,
    [isDisabled, acquisitionsData]
  );

  return (
    <LedgerDialog
      id="shares-ledger"
      title={titleLedger}
      dialogHeader={
        currentSecurity.name && fund ? (
          <Typography id="shares-ledger-subtitle" variant="subtitle2" color="text.secondary" sx={{ pb: 1 }}>
            {`${currentSecurity.name} (${fund.name})`}
          </Typography>
        ) : undefined
      }
      onSave={requestValidation}
      onClose={closeDialog}
      disabled={disabledStatus}>
      <Collapse in={isAlertVisible}>
        <Alert
          variant="outlined"
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setIsAlertVisible(false);
              }}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }>
          {errorMessage}
        </Alert>
        <br />
      </Collapse>

      <CustomContainer direction="column" wrap="nowrap">
        <Grid>
          <PurchasedTable
            investmentDates={investmentDates}
            setIsValid={setIsValidPurchasedTable}
            // NOT SURE WHAT TO DO WITH THESE CURRENCY PROPERTIES
            currency={currency}
            format={format}
            {...sharedProps}
            setPurchasedCells={setPurchasedCells}
            purchasedCells={purchasedCells}
            isWarrantOrOption={isWarrantOrOption}
          />
        </Grid>

        <Grid>
          <SoldTable
            setIsValid={setIsValidSoldTable}
            // NOT SURE WHAT TO DO WITH THESE CURRENCY PROPERTIES
            currency={currency}
            format={format}
            isLoanNote={!!loanNote}
            {...sharedProps}
            soldCells={soldCells}
            setSoldCells={setSoldCells}
          />
        </Grid>

        {modeledAfterConvertibleNote && !loanNote && (
          <Grid>
            <ConvertedTable
              setIsValid={setIsValidConvertedTable}
              currency={currency}
              {...sharedProps}
              convertedCells={convertedCells}
              setConvertedCells={setConvertedCells}
            />
          </Grid>
        )}
        <Grid>
          <Outstanding
            setIsValid={setIsValidOutstandingTable}
            // NOT SURE WHAT TO DO WITH THESE CURRENCY PROPERTIES
            currency={currency}
            {...sharedProps}
            outstandingShares={outstandingShares}
            outstandingCostBasis={outstandingCostBasis}
            outstandingAmount={outstandingAmount}
            relatedSecurity={relatedSecurityId!}
            format={format}
            outstandingLoanValue={outstandingLoanValue}
            isLoanNote={!!loanNote}
            purchasedCells={purchasedCells}
            soldCells={soldCells}
            outstandingValuesForDividends={outstandingValuesForDividends}
          />
        </Grid>
      </CustomContainer>
    </LedgerDialog>
  );
};

export default memo(SharesLedger);
